import { useEffect, useState, useCallback, useRef } from 'react';
import { loadPortOneSDK } from './portone';
import { PaymentService } from '@/supabase/payment/paymentService';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { SubscriptionPlan } from './types';
import { isUserCancelError } from './utils';
import { logError } from '@/lib/sentry';
import { supabase } from '@/supabase/client';

// PortOne SDK를 미리 로드하는 훅
export function usePortOneSDK() {
  const [isSDKLoaded, setIsSDKLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadSDK = useCallback(async () => {
    if (isSDKLoaded || isLoading) return;
    
    setIsLoading(true);
    try {
      await loadPortOneSDK();
      setIsSDKLoaded(true);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('SDK 로드에 실패했습니다'));
      logError('포트원 SDK 로드 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSDKLoaded, isLoading]);

  // 컴포넌트 마운트 시 자동 로드
  useEffect(() => {
    loadSDK();
  }, [loadSDK]);

  return { isSDKLoaded, isLoading, error, loadSDK };
}

// 구독 버튼 로직을 처리하는 훅
export function useSubscriptionButton(userId: string, planId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPrepared, setIsPrepared] = useState(false);
  const [isSamePlan, setIsSamePlan] = useState(false);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState<{ 
    amount: number;
    remainingDays: number;
    showDialog: boolean;
  } | null>(null);
  const [paymentService] = useState(() => new PaymentService());
  const { refreshSubscription, subscriptionPlan } = useSubscription();
  const { isSDKLoaded } = usePortOneSDK();

  // 업그레이드 다이얼로그 닫기
  const closeUpgradeDialog = useCallback(() => {
    setUpgradeInfo(prev => prev ? { ...prev, showDialog: false } : null);
  }, []);

  // 플랜/유저 데이터 사전 로드
  useEffect(() => {
    if (!userId || !planId) return;

    const prepareSubscription = async () => {
      try {
        // 현재 스탠다드 구독 중이고, 프리미엄으로 가려는 경우 업그레이드로 판단
        if (subscriptionPlan?.id === 'standard' && planId === 'premium') {
          setIsUpgrade(true);
          setIsPrepared(true);
          setIsSamePlan(false);
          return;
        }

        await paymentService.prepareSubscription(userId, planId);
        setIsPrepared(true);
        setIsSamePlan(false);
        setIsUpgrade(false);
      } catch (error) {
        if (error instanceof Error && error.message === '현재 이용 중인 플랜과 동일한 플랜입니다.') {
          setIsSamePlan(true);
          setIsPrepared(false);
          setIsUpgrade(false);
        } else {
          logError('구독 준비 실패:', error);
          toast.error(error instanceof Error ? error.message : '구독 준비 중 오류가 발생했습니다.');
        }
      }
    };

    prepareSubscription();
  }, [userId, planId, paymentService, subscriptionPlan]);

  // 업그레이드 정보 계산 함수
  const calculateUpgradeInfo = useCallback(async () => {
    if (!isUpgrade || !userId || subscriptionPlan?.id !== 'standard' || planId !== 'premium') {
      return false;
    }

    try {
      // 1. 현재 구독 정보와 플랜 정보 조회
      const currentSubscription = await paymentService.getCurrentSubscription(userId);
      if (!currentSubscription) {
        toast.error('현재 구독 정보를 찾을 수 없습니다.');
        return false;
      }

      // 플랜 정보 조회 (현재 플랜과 타겟 플랜)
      const standardPlan = await paymentService.getCurrentPlan(userId);
      const premiumPlan = await paymentService.getAllPlans().then(plans => 
        plans.find(plan => plan.id === 'premium')
      );
      
      if (!standardPlan || !premiumPlan) {
        toast.error('구독 플랜 정보를 찾을 수 없습니다.');
        return false;
      }
      
      // 2. 남은 일수 계산
      const now = new Date();
      const periodEnd = new Date(currentSubscription.period_end);
      const remainingDays = Math.max(0, Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      
      // 3. 업그레이드 금액 계산
      const priceDifference = premiumPlan.price - standardPlan.price;
      let upgradeAmount = Math.round((priceDifference * remainingDays) / 30);
      upgradeAmount = Math.max(100, upgradeAmount);
      
      // 4. 업그레이드 정보 설정
      setUpgradeInfo({
        amount: upgradeAmount,
        remainingDays: remainingDays,
        showDialog: true
      });
      
      return true;
    } catch (error) {
      logError('업그레이드 견적 계산 중 오류:', error);
      toast.error('업그레이드 금액 계산 중 오류가 발생했습니다.');
      return false;
    }
  }, [isUpgrade, userId, subscriptionPlan, planId, paymentService]);

  // 프리미엄 업그레이드 실행
  const executeUpgrade = useCallback(async () => {
    if (!userId || !isUpgrade || !upgradeInfo) return '';
    
    setIsLoading(true);
    try {
      // 업그레이드 로직 실행
      const paymentId = await paymentService.upgradeSubscription(userId, 'standard', 'premium');
      
      if (!paymentId) {
        console.log('업그레이드 결제가 취소되었습니다');
        return '';
      }
      
      console.log('업그레이드 성공:', paymentId);
      toast.success('프리미엄 플랜으로 성공적으로 업그레이드되었습니다');
      
      // 구독 정보 갱신
      await refreshSubscription();
      
      // 성공 후 페이지 새로고침
      window.location.reload();
      
      return paymentId;
    } catch (error) {
      logError('업그레이드 실패:', error);
      
      // 사용자 취소 에러 판단
      if (isUserCancelError(error)) {
        console.log('사용자가 결제창을 닫았습니다');
      } else if (error instanceof Error) {
        toast.error(error.message || '업그레이드 처리 중 오류가 발생했습니다');
      } else {
        toast.error('업그레이드 처리 중 알 수 없는 오류가 발생했습니다');
      }
      
      return '';
    } finally {
      setIsLoading(false);
    }
  }, [userId, isUpgrade, upgradeInfo, paymentService, refreshSubscription]);

  // 구독 처리 함수
  const handleSubscribe = useCallback(async () => {
    if (!isSDKLoaded) {
      toast.error('결제 시스템이 준비되지 않았습니다.');
      return;
    }
    
    // 업그레이드 케이스일 경우 먼저 견적을 계산하고 다이얼로그 표시
    if (isUpgrade && subscriptionPlan?.id === 'standard' && planId === 'premium') {
      await calculateUpgradeInfo();
      return; // 다이얼로그에서 확인 버튼 클릭 시 executeUpgrade 함수가 실행됨
    }
    
    setIsLoading(true);
    try {
      // 일반 구독 시작 (빌링키 발급, 결제, 구독 활성화 모두 포함)
      const newSubscription = await paymentService.startSubscription(userId, planId);
      
      if (!newSubscription) {
        // startSubscription이 null을 반환하면 사용자가 취소했거나 내부 오류 발생.
        // isUserCancelError로 상세 구분 가능 (startSubscription 내부에서 throw하는 경우)
        // 여기서는 startSubscription이 null을 반환하면 사용자 취소로 간주하거나,
        // startSubscription 내부에서 이미 적절한 toast를 띄웠다고 가정할 수 있음.
        console.log('구독 절차가 완료되지 않았습니다 (사용자 취소 또는 오류).');
        // toast.info('구독 절차가 취소되었습니다.'); // 필요시 메시지 추가
        return;
      }
      
      // startSubscription이 newSubscription 객체를 반환했다면 모든 과정이 성공한 것.
      // activateSubscription을 별도로 호출할 필요 없음.
      
      console.log('구독 성공:', newSubscription); // newSubscription 객체 전체 또는 newSubscription.id 로깅
      toast.success('구독이 성공적으로 시작되었습니다');
      
      // 구독 정보 갱신
      await refreshSubscription();
      
      // 성공 후 페이지 새로고침
      window.location.reload();
    } catch (error) {
      logError('구독 신청 실패:', error);
      
      // 사용자 취소 에러 판단 (isUserCancelError 유틸리티 함수 사용)
      if (isUserCancelError(error)) {
        console.log('사용자가 결제창을 닫았습니다');
        // 사용자 취소는 에러가 아니므로 토스트 메시지 표시하지 않음
      } else if (error instanceof Error) {
        toast.error(error.message || '결제 처리 중 오류가 발생했습니다');
      } else {
        toast.error('결제 처리 중 알 수 없는 오류가 발생했습니다');
      }
    } finally {
      setIsLoading(false);
    }
  }, [isSDKLoaded, userId, planId, paymentService, refreshSubscription, isUpgrade, subscriptionPlan, calculateUpgradeInfo]);

  return { 
    isLoading, 
    isPrepared, 
    isSamePlan, 
    isUpgrade, 
    upgradeInfo, 
    closeUpgradeDialog,
    executeUpgrade,
    handleSubscribe 
  };
}

// 연체(past_due) 구독 재결제 훅 — 등록된 빌링키로 카드창 없이 즉시 재결제
// 서버(user-billing-retry)가 2분 TTL 클레임으로 동시 호출(배너 버튼 + 카드 변경 자동
// 재결제)을 직렬화한다. 단 TTL을 넘겨 늘어진 요청은 재클레임될 수 있고 결제 idempotency
// key도 아직 없으므로, 이중 과금이 원천 차단되는 것은 아니다(발생 시 환불로 대응).
export function useBillingRetry() {
  const [isRetrying, setIsRetrying] = useState(false);
  // 진행 중 재호출 차단 — state는 같은 렌더 내 stale 가능성이 있어 ref로 가드
  const inFlightRef = useRef(false);
  const { refreshSubscription, subscription } = useSubscription();

  const retryBilling = useCallback(async (): Promise<boolean> => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    setIsRetrying(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        toast.error('인증 정보를 확인할 수 없습니다. 다시 로그인해 주세요.');
        return false;
      }

      // 화면에 표시 중인 연체 구독 행을 지정해 표시 행과 청구 행을 일치시킨다.
      // 서버가 본인 소유 + past_due 여부를 재검증하므로 stale이어도 안전
      const pastDueSubId = subscription?.status === 'past_due' ? subscription.id : undefined;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/user-billing-retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(pastDueSubId !== undefined ? { subscription_id: pastDueSubId } : {}),
      });

      let data: { success?: boolean; reason?: string; message?: string; pgMessage?: string; amount?: number } = {};
      try {
        data = await res.json();
      } catch {
        // 응답 파싱 실패 시 아래 공통 오류 처리로 진행
      }

      if (data.success) {
        toast.success(
          data.amount
            ? `재결제가 완료되었습니다. ${data.amount.toLocaleString()}원이 결제되었습니다.`
            : '재결제가 완료되었습니다. 구독이 다시 활성화되었습니다.'
        );
        await refreshSubscription();
        return true;
      }

      switch (data.reason) {
        case 'no_past_due_subscription':
          toast.info('연체 상태의 구독이 없습니다. 구독 상태를 새로고침합니다.');
          await refreshSubscription();
          break;
        case 'subscription_mismatch':
          toast.info('구독 정보가 갱신되었습니다. 상태를 새로고침한 뒤 다시 시도해 주세요.');
          await refreshSubscription();
          break;
        case 'retry_in_progress':
          toast.info('재결제가 이미 진행 중입니다. 잠시 후 구독 상태를 확인해 주세요.');
          break;
        case 'no_billing_key':
          toast.error('등록된 카드가 없습니다.', {
            description: '결제 카드 등록 후 다시 시도해 주세요.',
          });
          break;
        case 'payment_declined':
          toast.error('카드 결제가 거절되었습니다.', {
            description: `${data.pgMessage || data.message || ''} 카드 상태를 확인한 뒤 다시 시도하거나 카드를 변경해 주세요.`.trim(),
          });
          break;
        default:
          logError('재결제 처리 오류:', data);
          toast.error('재결제 처리 중 오류가 발생했습니다.', {
            description: '구독 상태가 반영되지 않으면 고객센터로 문의해 주세요.',
          });
      }
      return false;
    } catch (error) {
      logError('재결제 요청 실패:', error);
      toast.error('재결제 요청에 실패했습니다.', {
        description: '네트워크 상태를 확인하고 다시 시도해 주세요.',
      });
      return false;
    } finally {
      inFlightRef.current = false;
      setIsRetrying(false);
    }
  }, [refreshSubscription, subscription]);

  return { retryBilling, isRetrying };
}

// 카드 정보 로딩 훅
export function useCardInfo(userId: string | undefined, planId: string | null) {
  const [cardInfo, setCardInfo] = useState<{
    cardName: string;
    cardNumber: string;
    cardBrand?: string;
    cardIssuer?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshCardInfo = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // 다른 탭의 로그아웃/계정 전환이 BroadcastChannel로 전파되면 리마운트 없이
  // userId가 바뀐다 — 이전 계정 카드 정보 잔존과 늦게 도착한 응답의 오염을 막는다
  const currentUserIdRef = useRef(userId);
  useEffect(() => {
    if (currentUserIdRef.current !== userId) {
      currentUserIdRef.current = userId;
      setCardInfo(null);
      setIsLoaded(false);
    }
  }, [userId]);

  const fetchCardInfo = useCallback(async () => {
    const requestUserId = userId;
    if (!userId || !planId || !['standard', 'premium'].includes(planId)) {
      setCardInfo(null);
      setIsLoaded(true);
      return;
    }

    setIsLoading(true);

    try {
      // 서버(billing-key)가 토큰 본인 검증을 하므로 anon key가 아닌 유저 access token 필요.
      // body의 userId는 구버전 서버와의 전환기 호환용 (신서버는 토큰 기준으로 무시)
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        // finally에서 isLoading/isLoaded 정리됨
        setCardInfo(null);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/billing-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ userId }),
      });

      if (!res.ok) {
        throw new Error('카드 정보 조회에 실패했습니다');
      }

      const data = await res.json();

      // 응답 도착 시점에 계정이 바뀌었으면 폐기
      if (currentUserIdRef.current !== requestUserId) return;

      if (data.status === 'ISSUED' && data.card) {
        setCardInfo({
          cardName: data.card.cardName,
          cardNumber: data.card.cardNumber,
          cardBrand: data.card.cardBrand,
          cardIssuer: data.card.cardIssuer
        });
      } else {
        setCardInfo(null);
      }
    } catch (error) {
      logError('카드 정보 조회 오류:', error);
      if (currentUserIdRef.current === requestUserId) {
        setCardInfo(null);
      }
    } finally {
      // 전환 후 도착한 stale 완료가 새 계정의 재조회를 막지 않게 가드
      if (currentUserIdRef.current === requestUserId) {
        setIsLoading(false);
        setIsLoaded(true);
      }
    }
  }, [userId, planId]);
  
  // 최초 로드 및 리프레시 처리
  useEffect(() => {
    if ((userId && planId && !isLoaded) || refreshTrigger > 0) {
      fetchCardInfo();
    }
  }, [userId, planId, isLoaded, refreshTrigger, fetchCardInfo]);
  
  return { cardInfo, isLoading, isLoaded, refreshCardInfo };
}

// 구독 플랜 관리 훅
export function useSubscriptionPlans() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  
  const loadPlans = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    const paymentService = new PaymentService();
    
    try {
      const subscriptionPlans = await paymentService.getAllPlans();
      setPlans(subscriptionPlans);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('플랜 정보를 불러올 수 없습니다'));
      logError('플랜 정보 로드 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  useEffect(() => {
    loadPlans();
  }, [loadPlans]);
  
  return { plans, isLoading, error, loadPlans };
} 